import { get, omit, times } from 'lodash';
import { transparentize } from 'polished';
import { reduceObjectRegex } from '@monobits/core';
/**
 * Characters to reference a scale
 * @type {string}
 */

var SCALE_REF = '...';
/**
 * Get a color or color scale from an object
 *
 * @param  {Object} colors Object containing colors and color scales
 * @param  {string} ref String reference to an object key
 *
 * @example const _ = (source) => getColor(colors, source)
 * @example _`primary` // '#FFFFFF'
 * @example _`primary...` // {0: "#fffbf4", 1: "#ffeeca", __default: "#FFBD2B"}
 * @return {string|Object} A color string or scale object
 */

var getColor = function getColor(colors, ref) {
  var path = ref.toString();
  var find = get(colors, path.replace(SCALE_REF, ''));
  if (path.includes(SCALE_REF)) return find;
  if ('__default' in find) return find == null ? void 0 : find.__default;
  return find;
};
/**
 * Transparantize the highlight color
 *
 * @param  {string} color Valid color string
 * @param  {number} opacity=0.35 Opacity value
 * @return {string} A transparentized color value
 */


var makeHighlight = function makeHighlight(color, opacity) {
  if (opacity === void 0) {
    opacity = 0.35;
  }

  return transparentize(1 - opacity, color);
};
/**
 * Generate an array of shades
 *
 * @param  {func} style Polish.js color modifer
 * @param  {string} color Valid color string
 * @param  {array<number>} x Array of values to be generated
 * @return {array<string>} Array of shades
 */


var makeShadeArray = function makeShadeArray(style, color, x) {
  if (x === void 0) {
    x = [0.65, 0.35];
  }

  return x.map(function (value) {
    return style(value, color);
  });
};

function _extends() {
  _extends = Object.assign || function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];

      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }

    return target;
  };

  return _extends.apply(this, arguments);
}
/**
 * Generate a single color scale
 *
 * @param {sring} name Name of the color
 * @param {Object} properties
 * @param {string} properties.color Valid color string
 * @param {function} properties.style Polish.js color modifer
 * @param {Array.<number>} properties.x Array of values to be generated
 * @returns {Object} Color scale
 */


var makeSingleShade = function makeSingleShade(name, _ref) {
  var _ref2;

  var color = _ref.color,
      style = _ref.style,
      x = _ref.x;
  return _ref2 = {}, _ref2[name] = _extends({
    __default: color
  }, makeShadeArray(style, color, x)), _ref2;
};
/**
 * Generate a group of color scales
 *
 * @param  {[name: string, style: func, color: string]} [entries=[]] Array of multiple `makeSingleShade()` [name, style, color]
 * @param  {array} [x=[]] array of values to be generated
 * @return {Object} Color scales
 */


var makeShades = function makeShades(entries, x) {
  if (entries === void 0) {
    entries = {};
  }

  if (x === void 0) {
    x = [];
  }

  return Object.entries(entries).reduce(function (acc, _ref3) {
    var name = _ref3[0],
        _ref3$ = _ref3[1],
        style = _ref3$[0],
        color = _ref3$[1];
    return _extends({}, acc, makeSingleShade(name, {
      color: color,
      style: style,
      x: x
    }));
  }, {});
};

function _objectWithoutPropertiesLoose(source, excluded) {
  if (source == null) return {};
  var target = {};
  var sourceKeys = Object.keys(source);
  var key, i;

  for (i = 0; i < sourceKeys.length; i++) {
    key = sourceKeys[i];
    if (excluded.indexOf(key) >= 0) continue;
    target[key] = source[key];
  }

  return target;
}

var _excluded = ["modes", "colors"];
/**
 * Builds colors & modes
 *
 * @param  {Object} colorsProperties Object containing color properties
 * @param  {Object} colorsProperties.colors Object containing base color definitions
 * @param  {function} colorsProperties.modes Function returing an object with color definitions
 * @param  {string} colorsProperties.initialColorModeName Name of the initial color mode
 * @param  {...any} colorsProperties.props Rest of the props
 * @param  {Object} config The config Object containing `initialColorModeName`
 *
 * @return {Object} A theme-ui color & modes object
 */

var buildColors = function buildColors(_ref, config) {
  var preModes = _ref.modes,
      colors = _ref.colors,
      props = _objectWithoutPropertiesLoose(_ref, _excluded);

  var initial = (config == null ? void 0 : config.initialColorModeName) || 'light';
  var assembled = preModes(colors);
  var modes = reduceObjectRegex(assembled, [], {
    func: 'omit',
    regex: /(?!__default)(?:__.*)/g
  });
  var internalModes = reduceObjectRegex(assembled, [], {
    func: 'pick',
    regex: /(?!__default)(?:__.*)/g
  });
  return _extends({
    colors: _extends({}, modes[initial], {
      modes: omit(modes, initial)
    }),
    internals: internalModes || Object.keys(modes)
  }, props);
};
/**
 * Generate an array from a scale, omiting the default value
 *
 * @param  {object} scale A `makeShades()` array
 * @example makeTones({0: "#fffbf4", 1: "#ffeeca", __default: "#FFBD2B"})
 * @return {array<string>} ["#fffbf4", "#ffeeca"]
 */


var makeTones = function makeTones(scale) {
  var range = Object.keys(scale).filter(function (key) {
    return key !== '__default';
  });
  return times(range.length, function (i) {
    return scale[i];
  });
};
/**
 * Generate a grey scale and keys, following a light or dark color
 *
 * @param  {object} scale A `makeShades()` array
 * @return {object} Array of shades (light/dark)
 */


var makeGreyTones = function makeGreyTones(scale) {
  return {
    grey: makeTones(scale),
    muted: scale[0],
    neutral: scale[1],
    midtone: scale[2],
    subdued: scale[3]
  };
};

export { buildColors, getColor, makeGreyTones, makeHighlight, makeShadeArray, makeShades, makeTones };